'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, AlertCircle, Building2, FileText, ShieldCheck } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';


export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { signIn } = useAuth();
  const router = useRouter();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setLoading(true);
    try {
      await signIn(email, password);
      router.push('/');
      router.refresh();
    } catch (err: any) {
      setError(err?.message || 'Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-[52%] flex-col justify-between bg-card border-r border-border p-12 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-primary/5 blur-3xl" />
          <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-primary/8 blur-3xl" />
        </div>

        {/* Logo */}
        <div className="relative flex items-center gap-3">
          <AppLogo size={56} />
          <div>
            <span className="font-bold text-2xl text-foreground tracking-tight">Digital Plate</span>
            <p className="text-xs text-muted-foreground">Smart Billing. Better Business.</p>
          </div>
        </div>

        {/* Feature highlights */}
        <div className="relative space-y-8">
          <div>
            <h2 className="text-3xl font-700 text-foreground leading-tight mb-3">
              GST-compliant billing<br />built for Indian businesses
            </h2>
            <p className="text-muted-foreground text-base leading-relaxed max-w-sm">
              Manage invoices, purchases, expenses, inventory, and file GSTR returns — all from one place.
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                icon: FileText,
                title: 'Instant GST Invoices',
                desc: 'Create CGST/SGST/IGST invoices in seconds with auto tax calculation.',
              },
              {
                icon: ShieldCheck,
                title: 'GSTR Filing Ready',
                desc: 'Auto-populate GSTR-1, GSTR-3B, and reconcile ITC with ease.',
              },
              {
                icon: Building2,
                title: 'Multi-Business Support',
                desc: 'Manage multiple GSTINs and business entities from one account.',
              },
            ].map((f) => {
              const FIcon = f.icon;
              return (
                <div key={f.title} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <FIcon size={18} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-600 text-foreground">{f.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Demo credentials */}
        <div className="relative space-y-3">
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-secondary border border-border">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <ShieldCheck size={15} className="text-primary" />
            </div>
            <div>
              <p className="text-xs font-600 text-foreground">GST Council Compliant</p>
              <p className="text-xs text-muted-foreground">Aligned with latest CBIC guidelines &amp; e-invoicing norms</p>
            </div>
          </div>
          <div className="px-4 py-3 rounded-xl bg-primary/5 border border-primary/20">
            <p className="text-xs font-600 text-foreground mb-1.5">Admin Credentials</p>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Email: <span className="font-500 text-foreground">ghotikaramit@gmail.com</span></p>
              <p className="text-xs text-muted-foreground">Password: <span className="font-500 text-foreground">Bistro@2403</span></p>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel — login form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="flex lg:hidden items-center gap-2 mb-8">
            <AppLogo size={44} />
            <div>
              <span className="font-bold text-xl text-foreground">Digital Plate</span>
              <p className="text-xs text-muted-foreground">Smart Billing. Better Business.</p>
            </div>
          </div>

          <div className="mb-8">
            <h1 className="text-2xl font-700 text-foreground mb-1">Welcome back</h1>
            <p className="text-sm text-muted-foreground">Sign in to your Digital Plate account</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-warning/10 border border-warning/20 text-warning text-xs">
                <AlertCircle size={14} className="flex-shrink-0" />
                {error}
              </div>
            )}

            <div>
              <label className="block text-xs font-600 text-foreground mb-1.5">
                Email address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-input text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary transition-colors"
                placeholder="admin@kitchenly.in"
                autoComplete="email"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-600 text-foreground">Password</label>
                <button
                  type="button"
                  className="text-xs text-primary hover:underline font-500"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2.5 pr-10 rounded-lg border border-border bg-input text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary transition-colors"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="remember"
                className="rounded border-border"
              />
              <label htmlFor="remember" className="text-xs text-muted-foreground cursor-pointer">
                Keep me signed in for 30 days
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-600 hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  Signing in…
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            Don&apos;t have an account?{' '}
            <a
              href="mailto:ghotikaramit@gmail.com?subject=Request%20Access%20-%20Digital%20Plate&body=Hi%2C%20I%20would%20like%20to%20request%20access%20to%20Digital%20Plate."
              className="text-primary font-600 hover:underline"
            >
              Request access
            </a>
          </p>

          <p className="mt-8 text-center text-xs text-muted-foreground/60">
            © 2026 Digital Plate · GST Billing Suite
          </p>
        </div>
      </div>
    </div>
  );
}
